import { firstValueFrom } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ChiffresUtilesInstallation, Graphe, Installation as InstallationType, ListeInstallations, ReponseApi, Tag, TicketBrut, TicketStats } from './EntiteProjet';
import { Authentification } from './authentification';
import { environment } from '../../environment/environment';
import { extraireReponse } from './api';
import { PaginationMeta } from './pagination';
import { appliquerOptionsTickets, OptionsTickets } from './tickets-query';
import { extraireDefautsMateriels, type DefautsMateriel } from './defauts-materiels';
import { FiltresInterventions, InstallationInterventionsResponse } from './interventions';

export type ModeAffichageGraphique = 'jour' | 'mois' | 'annee';

export type DefautsPrecharges = {
  id: number;
  session: number;
  promesse: Promise<DefautsMateriel[]>;
};

/** Réponse de `GET /installation/:id/tickets` */
export type InstallationTicketsResponse = {
  tickets: TicketBrut[];
  pagination?: PaginationMeta;
};

/** Un point de mesure tel que retourné dans `ReponsePointMesures`. */
export type PointMesure = {
  idStructure: number;
  ref: string;
  special: string | null;
  nom: string;
};

/** Réponse de `GET /installation/:id/pm` */
export type ReponsePointMesures = {
  pointMesures?: PointMesure[];
};

/**
 * Une ligne de données retournée par `GET /installation/:id/indicateurs`.
 * Les noms de champs varient selon la version du backend.
 */
type LigneIndicateur = {
  date?: string;
  jour?: string;
  periode?: string;
  debut?: string;
  production?: number | string;
  production_cumulee?: number | string;
  prod?: number | string;
  prod_calc?: number | string;
  reference?: number | string;
  reference_cumulee?: number | string;
  ref?: number | string;
  autoconsommation?: number | string;
  auto_consommation?: number | string;
  autoconso?: number | string;
  productible_estime?: number | string;
  productibleEstime?: number | string;
};

/** Métadonnées de période optionnellement incluses dans `ReponseIndicateurs`. */
type MetadonneesIndicateurs = {
  debut?: string;
  date_debut?: string;
  start?: string;
  start_date?: string;
  fin?: string;
  date_fin?: string;
  end?: string;
  end_date?: string;
};

/**
 * Réponse de `GET /installation/:id/indicateurs?type=production-cumulee`.
 * Peut être un tableau de lignes direct ou un objet enveloppant avec métadonnées.
 */
export type ReponseIndicateurs = LigneIndicateur[] | {
  data?: LigneIndicateur[];
  datas?: LigneIndicateur[];
  items?: LigneIndicateur[];
  metadatas?: MetadonneesIndicateurs;
};

/**
 * Une ligne de mesure temporelle retournée par `GET /installation/:id/data`.
 * Les noms de champs de date et de valeur varient selon le format demandé.
 * La signature d'index `[key: string]` permet d'accéder aux colonnes par code métier.
 */
export type LigneDonnees = {
  date?: string | number;
  valeur?: number | string;
  value?: number | string;
  mesure?: number | string;
  code?: string;
  nom?: string;
  [key: string]: unknown;
};

/** Métadonnées d'un code métier (point de mesure) retournées dans `sources`. */
export type SourceDonnee = {
  nom?: string;
  unite?: string;
};

/**
 * Réponse de `GET /installation/:id/data`.
 * Peut être un tableau de lignes direct ou un objet avec une clé `datas` contenant le tableau.
 * `sources` associe chaque code métier demandé (ex. `MR1`) à son unité (ex. `Wh`).
 */
export type DonneesInstallation = LigneDonnees[] | {
  sources?: Record<string, SourceDonnee>;
  datas?: LigneDonnees[];
};

@Injectable({
  providedIn: 'root',
})
export class Installation {
  private cacheInstallation = new Map<number, { data: InstallationType | null; ts: number }>();
  private readonly CACHE_TTL_MS = 5000;

  constructor(private auth: Authentification, private http: HttpClient) {
    this.auth.sessionChanges.subscribe(() => this.cacheInstallation.clear());
  }

  async getInstallationInterventions(id: number, filtres: FiltresInterventions = {}): Promise<InstallationInterventionsResponse | null> {
    let params = new HttpParams();
    if (filtres.debut) params = params.set('debut', filtres.debut);
    if (filtres.fin) params = params.set('fin', filtres.fin);
    const res = await firstValueFrom(this.http.get<ReponseApi<InstallationInterventionsResponse>>(
      `${environment.apiUrl}/installation/${id}/interventions`,
      { ...this.auth.prepareHeaders(), params },
    ));
    return extraireReponse(res);
  }

  getInstallationDataRange(mode: ModeAffichageGraphique = 'jour', fin?: Date) {
    const ref = fin ? new Date(fin) : new Date();
    let debut: Date;
    let finDate: Date;
    let format: string;

    switch (mode) {
      case 'mois':
        debut = new Date(ref.getFullYear(), ref.getMonth(), 1);
        finDate = new Date(ref.getFullYear(), ref.getMonth() + 1, 0);
        format = 'jour';
        break;
      case 'annee': {
        const annee = ref.getFullYear();
        return {
          debut: new Date(annee, 0, 1),
          fin: new Date(annee, 11, 31),
          debutApi: `${annee}-01`,
          finApi: `${annee}-12`,
          format: 'mois',
        };
      }
      default:
        debut = new Date(ref); debut.setDate(ref.getDate() - 1);
        finDate = new Date(ref); finDate.setDate(ref.getDate() + 1);
        format = 'minute';
    }

    const fmt = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

    return {
      debut,
      fin: finDate,
      debutApi: fmt(debut),
      finApi: fmt(finDate),
      format,
    };
  }

  getProductionCumuleeRange(mode: ModeAffichageGraphique = 'mois', fin?: Date) {
    const ref = fin ? new Date(fin) : new Date();
    const annee = ref.getFullYear();
    const mois = String(ref.getMonth() + 1).padStart(2, '0');

    if (mode === 'annee') {
      return {
        debutApi: String(annee),
        format: 'mois',
      };
    }

    return {
      debutApi: `${annee}-${mois}`,
      format: 'jour',
    };
  }

  /**
   * Récupère la liste des installations depuis l'API.
   * Si `parcId` est fourni, la liste est filtrée sur ce parc.
   * Les erreurs HTTP et métier sont propagées au composant appelant.
   */
  async listeInstallations(parcId?: number): Promise<ListeInstallations | null> {
    const url = `${environment.apiUrl}/installation`;
    const options = {
      ...this.auth.prepareHeaders(),
      params: parcId != null ? new HttpParams().set('parc', parcId) : undefined,
    };
    try {
      const res = await firstValueFrom(
        this.http.get<ReponseApi<ListeInstallations>>(url, options)
      );
      return extraireReponse(res);
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Récupère le détail d'une installation via son identifiant.
   * Les erreurs HTTP et métier sont propagées au composant appelant.
   */
  async getInstallation(id: number): Promise<InstallationType | null> {
    const sessionVersion = this.auth.sessionVersion;
    const cached = this.cacheInstallation.get(id);
    if (cached && Date.now() - cached.ts < this.CACHE_TTL_MS) return cached.data;

    const url = `${environment.apiUrl}/installation/${id}`;
    try {
      const res = await firstValueFrom(
        this.http.get<ReponseApi<InstallationType>>(url, this.auth.prepareHeaders())
      );
      const reponse = extraireReponse(res);
      const normalise = reponse ? this.normaliserInstallation(reponse) : null;
      if (sessionVersion !== this.auth.sessionVersion) return null;
      this.cacheInstallation.set(id, { data: normalise, ts: Date.now() });
      return normalise;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Récupère les données PM d'une installation via son identifiant.
   * Les erreurs HTTP et métier sont propagées au composant appelant.
   */
  async getInstallationPm(id: number): Promise<ReponsePointMesures | null> {
    const url = `${environment.apiUrl}/installation/${id}/pm`;
    try {
      const res = await firstValueFrom(
        this.http.get<ReponseApi<ReponsePointMesures>>(url, this.auth.prepareHeaders())
      );
      return extraireReponse(res);
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Récupère les chiffres utiles (puissance moyenne, énergie cumulée, CO2 évité, revenus cumulés)
   * d'une installation via son identifiant.
   * Les erreurs HTTP et métier sont propagées au composant appelant.
   */
  async getChiffresUtiles(id: number): Promise<ChiffresUtilesInstallation | null> {
    const url = `${environment.apiUrl}/installation/${id}/chiffres-utiles`;
    try {
      const res = await firstValueFrom(
        this.http.get<ReponseApi<ChiffresUtilesInstallation>>(url, this.auth.prepareHeaders())
      );
      return extraireReponse(res);
    } catch (error: any) {
      throw error;
    }
  }

  async getIndicateursProductionCumulee(id: number, debut: string, format = 'jour'): Promise<ReponseIndicateurs | null> {
    const url = `${environment.apiUrl}/installation/${id}/indicateurs`;
    const params = new HttpParams()
      .set('type', 'production-cumulee')
      .set('format', format)
      .set('debut', debut);
    console.log(`[getIndicateursProductionCumulee] ${url}?${params.toString()}`);
    try {
      const res = await firstValueFrom(
        this.http.get<ReponseApi<ReponseIndicateurs>>(url, {
          ...this.auth.prepareHeaders(),
          params,
        })
      );
      return extraireReponse(res);
    } catch (error: any) {
      throw error;
    }
  }

  async getInstallationData(id: number, codes: string[], mode: ModeAffichageGraphique = 'jour', fin?: Date): Promise<DonneesInstallation | null> {
    const url = `${environment.apiUrl}/installation/${id}/data`;
    const plage = this.getInstallationDataRange(mode, fin);

    const params = new HttpParams()
      .set('code', codes.join(','))
      .set('format', plage.format)
      .set('vue', 'tableau')
      .set('debut', plage.debutApi)
      .set('fin', plage.finApi);

    try {
      const res = await firstValueFrom(
        this.http.get<ReponseApi<DonneesInstallation>>(url, {
          ...this.auth.prepareHeaders(),
          params,
        })
      );
      return extraireReponse(res);
    } catch (error: any) {
      throw error;
    }
  }

  async getInstallationDefauts(id: number): Promise<DefautsMateriel[]> {
    const res = await firstValueFrom(
      this.http.get<ReponseApi>(
        `${environment.apiUrl}/installation/${id}/defauts`,
        this.auth.prepareHeaders(),
      ),
    );
    // La vue Équipements consomme exclusivement la liste des matériels.
    return extraireDefautsMateriels(extraireReponse(res));
  }

  async getInstallationTicketStats(id: number): Promise<TicketStats | null> {
    const url = `${environment.apiUrl}/installation/${id}/tickets/stats`;
    try {
      const res = await firstValueFrom(
        this.http.get<ReponseApi<TicketStats>>(url, this.auth.prepareHeaders())
      );
      return extraireReponse(res);
    } catch (error: any) {
      throw error;
    }
  }

  async getInstallationTickets(
    id: number,
    etat?: string,
    page?: number,
    limit?: number,
    search?: string,
    optionsTickets?: OptionsTickets,
  ): Promise<InstallationTicketsResponse | null> {
    const url = `${environment.apiUrl}/installation/${id}/tickets`;
    let params = appliquerOptionsTickets(new HttpParams(), optionsTickets);
    if (etat) {
      params = params.set('etat', etat);
    }
    if (page != null) {
      params = params.set('page', page);
    }
    if (limit != null) {
      params = params.set('limit', limit);
    }
    if (search) {
      params = params.set('search', search);
    }
    const options = {
      ...this.auth.prepareHeaders(),
      params,
    };

    try {
      const res = await firstValueFrom(
        this.http.get<ReponseApi<InstallationTicketsResponse | unknown[]>>(url, options)
      );
      const reponse = extraireReponse(res);
      if (reponse === null) return null;
      if (Array.isArray(reponse)) return { tickets: reponse as TicketBrut[] };
      const payload = reponse as InstallationTicketsResponse;
      return {
        tickets: Array.isArray(payload.tickets) ? payload.tickets : [],
        pagination: payload.pagination,
      };
    } catch (error: any) {
      throw error;
    }
  }

  async getGraphes(id: number): Promise<Graphe[] | null> {
    const url = `${environment.apiUrl}/installation/${id}/graphes`;
    try {
      const res = await firstValueFrom(
        this.http.get<ReponseApi<Graphe[]>>(url, this.auth.prepareHeaders())
      );
      return extraireReponse(res);
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Centralise l'adaptation du contrat backend vers le modele frontend
   * pour que toute evolution du payload Installation soit geree ici.
   */
  private normaliserInstallation(installation: InstallationType): InstallationType {
    return {
      ...installation,
      etat: installation.etat ?? {niveau: 0, couleur: '', hors_ligne: false, arret: false, tickets_avec_pertes: false, tickets_sans_pertes: false },
      tags: this.normaliserTags(installation.tags),
      DEIE: Array.isArray(installation.DEIE) || !installation.DEIE
        ? null
        : { date: installation.DEIE.date, etat: { ...installation.DEIE.etat } },
    };
  }

  private normaliserTags(tags: unknown): Tag[] {
    if (!Array.isArray(tags)) {
      throw new Error('Tags invalide: installation.tags doit etre un tableau.');
    }

    return tags.map((tag, index) => {
      if (!tag || typeof tag !== 'object') {
        throw new Error(`Tag invalide: tags[${index}] doit etre un objet.`);
      }

      const candidat = tag as Record<string, unknown>;

      if (typeof candidat['id'] !== 'string') {
        throw new Error(`Tag invalide: tags[${index}].id doit etre une chaine.`);
      }

      if (typeof candidat['libelle'] !== 'string') {
        throw new Error(`Tag invalide: tags[${index}].libelle doit etre une chaine.`);
      }

      if (typeof candidat['couleur'] !== 'string') {
        throw new Error(`Tag invalide: tags[${index}].couleur doit etre une chaine.`);
      }

      if (typeof candidat['priorite'] !== 'string') {
        throw new Error(`Tag invalide: tags[${index}].priorite doit etre une chaine.`);
      }

      const type = candidat['type'];
      if (type !== null && typeof type !== 'string') {
        throw new Error(`Tag invalide: tags[${index}].type doit etre une chaine ou null.`);
      }

      const lie = candidat['lie'];
      if (lie !== null && typeof lie !== 'string') {
        throw new Error(`Tag invalide: tags[${index}].lie doit etre une chaine ou null.`);
      }

      return {
        id: candidat['id'],
        libelle: candidat['libelle'],
        couleur: candidat['couleur'],
        priorite: candidat['priorite'],
        type,
        lie,
      };
    });
  }
}
